import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';

const API_BASE = import.meta.env.VITE_API_BASE ?? window.location.origin;
const getAuthToken = () => localStorage.getItem('erixone_token') ?? localStorage.getItem('ericone_token') ?? localStorage.getItem('eric_token') ?? '';

export function ExtractorsPage() {
  const { tenant } = useAuth();
  const [extractors, setExtractors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
  const [selectedExtractor, setSelectedExtractor] = useState<any>(null);
  
  // Create form state
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [fields, setFields] = useState<any[]>([{ name: '', type: 'STRING', description: '' }]);
  const [creating, setCreating] = useState(false);

  // Detail state
  const [extractedData, setExtractedData] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchExtractors();
  }, [tenant]);

  const fetchExtractors = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/extractors`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      const data = await res.json();
      if (data.extractors) setExtractors(data.extractors);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    
    // Convert fields to JSON schema
    const properties: any = {};
    const required: string[] = [];
    fields.forEach(f => {
      if (f.name) {
        properties[f.name] = { type: f.type, description: f.description };
        required.push(f.name);
      }
    });
    
    const schema_definition = {
      type: "OBJECT",
      properties,
      required
    };

    try {
      const res = await fetch(`${API_BASE}/api/extractors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ name: newName, description: newDesc, schema_definition })
      });
      const data = await res.json();
      if (data.extractor) {
        setExtractors([data.extractor, ...extractors]);
        setView('list');
        setNewName('');
        setNewDesc('');
        setFields([{ name: '', type: 'STRING', description: '' }]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const loadExtractorData = async (extractor: any) => {
    setSelectedExtractor(extractor);
    setView('detail');
    setExtractedData([]);
    try {
      const res = await fetch(`${API_BASE}/api/extractors/${extractor.id}/data`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      const data = await res.json();
      if (data.data) {
        setExtractedData(data.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('extractor_id', selectedExtractor.id);

    try {
      const res = await fetch(`${API_BASE}/api/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        body: formData
      });
      
      if (res.ok) {
        alert('File uploaded to extractor queue! It will appear here once processed.');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const exportExcel = () => {
    if (!extractedData.length) return;
    
    // Simple CSV export for now
    const keys = Object.keys(selectedExtractor.schema_definition.properties || {});
    const headers = ['File Name', 'Created At', ...keys];
    
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
    csvContent += headers.join(",") + "\r\n";
    
    extractedData.forEach(row => {
      const fileData = [row.filename, new Date(row.created_at).toLocaleString()];
      const cols = keys.map(k => {
        let val = row.data[k] || '';
        if (typeof val === 'string') val = val.replace(/"/g, '""');
        return `"${val}"`;
      });
      csvContent += [...fileData, ...cols].join(",") + "\r\n";
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${selectedExtractor.name}_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div style={{ padding: '24px', color: '#fff' }}>Loading extractors...</div>;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', color: '#f1f5f9' }}>
      {view === 'list' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 600 }}>Data Extractors</h1>
            <button 
              onClick={() => setView('create')}
              style={{
                background: 'var(--accent-purple)',
                color: '#fff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              + Create Extractor
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {extractors.length === 0 ? (
              <div style={{ padding: '32px', background: 'var(--bg-glass)', borderRadius: '12px', gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-muted)' }}>
                You don't have any extractors yet. Create one to start mining data!
              </div>
            ) : (
              extractors.map(ext => (
                <div 
                  key={ext.id} 
                  onClick={() => loadExtractorData(ext)}
                  style={{
                    background: 'var(--bg-glass)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                  onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent-purple)'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'}
                >
                  <h3 style={{ margin: 0, fontSize: '18px', color: '#fff' }}>{ext.name}</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>{ext.description || 'No description'}</p>
                  <div style={{ marginTop: 'auto', paddingTop: '12px', fontSize: '12px', color: 'var(--accent-cyan)' }}>
                    {Object.keys(ext.schema_definition?.properties || {}).length} fields defined
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {view === 'create' && (
        <div style={{ background: 'var(--bg-glass)', padding: '32px', borderRadius: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>←</button>
            <h2 style={{ margin: 0, fontSize: '20px' }}>Create New Extractor</h2>
          </div>
          
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Extractor Name</label>
              <input 
                required
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. VAT Invoice Extractor"
                style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', padding: '10px', borderRadius: '6px', color: '#fff' }}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Description</label>
              <input 
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                placeholder="Extracts total amount, date, and vendor from invoices"
                style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', padding: '10px', borderRadius: '6px', color: '#fff' }}
              />
            </div>

            <div>
              <label style={{ fontSize: '14px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Fields to Extract</label>
              {fields.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                  <input 
                    placeholder="Field name (e.g. total_amount)" 
                    value={f.name}
                    onChange={e => { const nf = [...fields]; nf[i].name = e.target.value; setFields(nf); }}
                    style={{ flex: 1, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', padding: '8px', borderRadius: '6px', color: '#fff' }}
                  />
                  <select 
                    value={f.type}
                    onChange={e => { const nf = [...fields]; nf[i].type = e.target.value; setFields(nf); }}
                    style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', padding: '8px', borderRadius: '6px', color: '#fff', width: '120px' }}
                  >
                    <option value="STRING">String</option>
                    <option value="NUMBER">Number</option>
                    <option value="BOOLEAN">Boolean</option>
                  </select>
                  <input 
                    placeholder="Description (optional)" 
                    value={f.description}
                    onChange={e => { const nf = [...fields]; nf[i].description = e.target.value; setFields(nf); }}
                    style={{ flex: 2, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', padding: '8px', borderRadius: '6px', color: '#fff' }}
                  />
                  <button type="button" onClick={() => setFields(fields.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer' }}>✕</button>
                </div>
              ))}
              <button 
                type="button" 
                onClick={() => setFields([...fields, { name: '', type: 'STRING', description: '' }])}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
              >
                + Add Field
              </button>
            </div>

            <div style={{ marginTop: '16px' }}>
              <button 
                type="submit" 
                disabled={creating}
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                  color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600
                }}
              >
                {creating ? 'Creating...' : 'Create Extractor'}
              </button>
            </div>
          </form>
        </div>
      )}

      {view === 'detail' && selectedExtractor && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', height: 'calc(100vh - 100px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>←</button>
              <h2 style={{ margin: 0, fontSize: '20px' }}>{selectedExtractor.name}</h2>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={exportExcel} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                Download CSV
              </button>
              <label style={{ background: 'var(--accent-purple)', color: '#fff', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                {uploading ? 'Uploading...' : 'Upload Document'}
                <input type="file" ref={fileInputRef} onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
            </div>
          </div>

          <div style={{ background: 'var(--bg-glass)', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead style={{ background: 'rgba(0,0,0,0.4)' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>File Name</th>
                    <th style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Processed Date</th>
                    {Object.keys(selectedExtractor.schema_definition?.properties || {}).map(k => (
                      <th key={k} style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {extractedData.length === 0 ? (
                    <tr>
                      <td colSpan={100} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                        No data extracted yet. Upload a document to begin!
                      </td>
                    </tr>
                  ) : (
                    extractedData.map(row => (
                      <tr key={row.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '12px 16px', color: 'var(--accent-cyan)' }}>{row.filename}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>{new Date(row.created_at).toLocaleString()}</td>
                        {Object.keys(selectedExtractor.schema_definition?.properties || {}).map(k => (
                          <td key={k} style={{ padding: '12px 16px' }}>{String(row.data[k] ?? '-')}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
